import React, { useMemo } from 'react';

import { useResponsive } from '@garnish/core';

import { Navbar } from '../../../../../components/Navbar';
import { useIsFoldableDevice } from '../../../../../hooks/useIsFoldableDevice';
import {
  CONTENT_WITH_BG_COLOR_STYLES,
  CONTENT_WITHOUT_BG_STYLES,
  DEFAULT_SCREEN_OPTIONS,
  type ScreenOptions,
  type ScreenOptionsMap,
} from '../../../../constants';
import { useModalsPresentation } from '../../../useModalsPresentation';

// ─────────────────────────────────────────────────────────────────────────────

export const useTabStackOptions = () => {
  const { match } = useResponsive();
  const modalsPresentation = useModalsPresentation();
  const isFoldableDevice = useIsFoldableDevice();

  return useMemo(() => {
    return {
      //
      // ─── Main Stack Options (applied to all screens) ─────────────────────

      Stack: { ...DEFAULT_SCREEN_OPTIONS, headerShown: false },

      // ─── Individual Tabs ─────────────────────────────────────────────────

      Tabs: { ...DEFAULT_SCREEN_OPTIONS },
      Modals: {
        presentation: modalsPresentation,
        contentStyle: match([CONTENT_WITH_BG_COLOR_STYLES, CONTENT_WITHOUT_BG_STYLES]),
      },
      Auth: {
        ...DEFAULT_SCREEN_OPTIONS,
        presentation: isFoldableDevice ? 'transparentModal' : 'modal',
      },
      TransparentModals: {
        presentation: 'transparentModal',
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
        animation: 'fade',
      },

      // ─── Not Found ─────────────────────────────────────────────────────

      NotFound: {
        ...DEFAULT_SCREEN_OPTIONS,
        headerShown: match([false, true]),
        header: match([undefined, renderNavbar]),
      },

      // ─── ID.me Redirect ────────────────────────────────────────────────────

      IdMeRedirect: { ...DEFAULT_SCREEN_OPTIONS, headerShown: false },
    } satisfies TabStackOptionsMap;
  }, [isFoldableDevice, match, modalsPresentation]);
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function renderNavbar() {
  return <Navbar />;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ScreenOptionsCallback = () => ScreenOptions;
type TabStackOptionsMap = Record<string, ScreenOptionsMap[string] | ScreenOptionsCallback>;
